import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Stretches to the width of its container; used for form submits on mobile. */
    fullWidth?: boolean;
    /**
     * Shows a spinner and blocks interaction. The label stays in the DOM so the
     * button does not change width mid-request.
     */
    isLoading?: boolean;
    /** Announced while `isLoading`. Defaults to a generic Dutch phrase. */
    loadingLabel?: string;
    iconStart?: ReactNode;
    iconEnd?: ReactNode;
    /** Renders a square icon button. `aria-label` then becomes required. */
    iconOnly?: boolean;
}

/**
 * The one button in the product.
 *
 * Variants map to intent rather than to colour, so a screen never has to reach
 * for a raw token to make a button look right.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'primary',
        size = 'md',
        fullWidth = false,
        isLoading = false,
        loadingLabel = 'Bezig…',
        iconStart,
        iconEnd,
        iconOnly = false,
        disabled,
        children,
        type = 'button',
        ...rest
    },
    ref,
) {
    const classes = [
        styles.button,
        styles[variant],
        size !== 'md' ? styles[size] : null,
        fullWidth ? styles.fullWidth : null,
        iconOnly ? styles.iconOnly : null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            {...rest}
            ref={ref}
            type={type}
            className={classes}
            // Kept focusable while loading so focus is not thrown to the body
            // mid-submit; the click is blocked by `disabled` instead.
            disabled={disabled === true || isLoading}
            aria-busy={isLoading || undefined}
        >
            {isLoading ? (
                <>
                    <span className={styles.spinner} aria-hidden="true" />
                    <span className="visually-hidden">{loadingLabel}</span>
                </>
            ) : (
                iconStart
            )}
            {!iconOnly && children}
            {!isLoading && iconEnd}
        </button>
    );
});

export interface ButtonLinkProps extends Omit<LinkProps, 'className'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    fullWidth?: boolean;
    iconStart?: ReactNode;
    iconEnd?: ReactNode;
}

/**
 * A navigation control that looks like a button.
 *
 * Separate from {@link Button} on purpose: something that changes the URL must
 * be an anchor so it can be opened in a new tab, copied, and announced as a
 * link — a <button> with an onClick that navigates does none of that.
 */
export function ButtonLink({
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    iconStart,
    iconEnd,
    children,
    ...rest
}: ButtonLinkProps) {
    const classes = [
        styles.button,
        styles[variant],
        size !== 'md' ? styles[size] : null,
        fullWidth ? styles.fullWidth : null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Link {...rest} className={classes}>
            {iconStart}
            {children}
            {iconEnd}
        </Link>
    );
}
